import { execOnLoad, getA11yTranslations } from '@general/helpers';
import Swiper from 'swiper';
import { A11y, EffectFade, Navigation, Pagination } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';
import breakpoints from 'breakpoints';
import { throttle } from 'lodash';
import { addToWishlist, deleteFromWishlist } from '@components/wishlist';
import '../../snippets/quick-buy/quick-buy.js';

if (!window.dAgency) window.dAgency = {};

// Initialize quick-buy listener to show quick-buy modal
if (!window.dAgency.quickBuyListenerInitialized) {
  document.addEventListener('click', async (e) => {
    const quickBuyBtn = e.target.closest('.product-tile__quick-buy--modal-btn');
    if (!quickBuyBtn) return;

    const handle = quickBuyBtn.dataset.handle;
    if (!handle) return;

    const modalWrapper = document.getElementById('quick-view-modal');
    if (!modalWrapper) return;

    quickBuyBtn.classList.add('loading');

    // Fetch product quick-buy HTML via the sr-quick-buy section (`?sections=sr-quick-buy`)
    const root = window.Shopify?.routes?.root || '/';
    try {
      const res = await fetch(`${root}products/${handle}?sections=sr-quick-buy`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const html = data['sr-quick-buy'];
      if (!html) throw new Error('No sr-quick-buy section in response');

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;

      const content = wrapper.firstElementChild;
      if (!content) throw new Error('No content found in section');

      modalWrapper.replaceChildren(content);

      const modal = modalWrapper.querySelector('.quick-buy__container');
      if (modal && typeof window?.dAgency?.initQuickBuy === 'function') {
        window.dAgency.initQuickBuy(modal);
      }
    } catch (err) {
      console.error('Error loading quick-buy modal', err);
    } finally {
      quickBuyBtn.classList.remove('loading');
    }
  });

  // Loaded only once
  window.dAgency.quickBuyListenerInitialized = true;
}

window.dAgency.initProductTile = (context) => {
  /**
   * Container resolution with sequential fallback strategy
   * Supports multiple context types from static templates to dynamic Section Rendering API
   *
   * Search priority:
   * 1. Direct match - fastest (static product tiles)
   * 2. Parent traversal - template wrappers
   * 3. Child query - dynamic sections (recently viewed carousel)
   */
  let snippetContainer = null;

  // 1. Direct match - fastest (static product tiles)
  snippetContainer = context.classList.contains('product-tile__container') ? context : null;

  // 2. Parent traversal - template wrappers
  if (!snippetContainer) snippetContainer = context.closest('.product-tile__container');

  // 3. Child query - dynamic sections (recently viewed carousel)
  if (!snippetContainer) snippetContainer = context.querySelector('.product-tile__container');

  if (!snippetContainer || snippetContainer.classList.contains('product-tile__container--initialized')) return;

  // Tag transformations
  const tagMappings = {
    productTitleTag: '.product-tile__title',
    productVendorTag: '.product-tile__vendor',
  };
  Object.entries(tagMappings).forEach(([datasetKey, selector]) => {
    const tagValue = snippetContainer.dataset[datasetKey];
    if (!tagValue) return;

    const element = snippetContainer.querySelector(selector);
    if (!element || tagValue === element.tagName.toLowerCase()) return;

    const newElement = document.createElement(tagValue);
    newElement.className = element.className;
    newElement.innerHTML = element.innerHTML;
    element.parentNode.replaceChild(newElement, element);

    // Remove processed dataset attribute
    delete snippetContainer.dataset[datasetKey];
  });

  const swiperWrapper = snippetContainer.querySelector('.product-tile__gallery > .swiper-wrapper');
  if (swiperWrapper && swiperWrapper.querySelectorAll('.swiper-slide').length > 1) {
    const swiperContainer = swiperWrapper.closest('.product-tile__gallery');
    const pagination = swiperContainer.querySelector('.product-tile__gallery-pagination');
    const options = {
      modules: [
        ...(window.dAgency.translations ? [A11y] : []),
        EffectFade,
        Navigation,
        ...(pagination ? [Pagination] : []),
      ],
      crossFade: true,
      effect: 'fade',
      navigation: {
        prevEl: swiperContainer.querySelector('.product-tile__gallery-arrow--left'),
        nextEl: swiperContainer.querySelector('.product-tile__gallery-arrow--right'),
      },
      ...(pagination
        ? {
            pagination: {
              el: pagination,
            },
          }
        : {}),
      ...getA11yTranslations(),
    };
    var autoChange = true;
    var changeEnter = false;
    var changeLeave = false;
    const swiper = new Swiper(swiperContainer, options);
    swiperContainer.addEventListener('mouseenter', () => {
      if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
      changeEnter = true;
      swiper.slideTo(1);
    });

    swiperContainer.addEventListener('mouseleave', () => {
      if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
      changeLeave = true;
      swiper.slideTo(0);
    });

    swiper.on('slideChangeTransitionStart', () => {
      if (!changeEnter && !changeLeave) {
        autoChange = false;
        return;
      }
      if (changeEnter) changeEnter = false;
      if (changeLeave) changeLeave = false;
    });
  }

  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');

  snippetContainer.classList.add('product-tile__container--initialized');

  if (wishlistButton) {
    const toggleLoading = throttle(() => {
      wishlistButton.classList.toggle('loading');
    }, 500);
    wishlistButton.addEventListener('click', async (e) => {
      e.preventDefault();
      toggleLoading();
      const variantID = wishlistButton?.dataset?.wishlistVariantId;
      const masterID = wishlistButton?.dataset?.wishlistProductId;
      const productURL = wishlistButton?.dataset?.wishlistProductUrl;
      const price = wishlistButton?.dataset?.wishlistPrice;
      const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

      if (wishlistButton?.dataset?.wishlistStatus === 'active') {
        await deleteFromWishlist({ variantID, masterID, productURL });
        wishlistButton.dataset.wishlistStatus = 'inactive';
      } else {
        await addToWishlist({
          variantID,
          masterID,
          productURL,
          compareAtPrice,
          price,
        });
        wishlistButton.dataset.wishlistStatus = 'active';
      }
      toggleLoading();
    });
  }

  // Mouse move effect for specific tag element in product tile
  // const tags = snippetContainer.querySelector('.product-tile__tags');
  // const infoH = snippetContainer.querySelector('.product-tile__info');

  // if (tags && window.innerWidth > parseInt(breakpoints.lg)) {
  //   snippetContainer.addEventListener('mousemove', (e) => {
  //     const rect = snippetContainer.getBoundingClientRect();
  //     const y = e.clientY - rect.top;
  //     const height = rect.height;

  //     // Calculate percentage (0 at top, 1 at bottom)
  //     const percentage = y / height;

  //     // We want: Mouse Top (0) -> Tags Up (Negative TranslateY)
  //     // Mouse Bottom (1) -> Tags Down (0)
  //     // Use container height for 1:1 movement, but stop a bit earlier (e.g. -60px)
  //     const maxOffset = height - (infoH.clientHeight + 20);
  //     const translateY = percentage * maxOffset;

  //     tags.style.transform = `translateY(${translateY}px)`;
  //   });

  //   snippetContainer.addEventListener('mouseleave', () => {
  //     tags.style.transform = `translateY(0px)`;
  //   });
  // }
};

execOnLoad(window.dAgency.initProductTile, '.product-tile__container');
